/**
 * The symbol dropdown on the DOM, the order pad and every chart pane (owner 2026-09-28: "the DOM
 * doesn't let me change the ticker — look how Tradovate allows multiple tickers on the DOM" and
 * "add in the most common (and frequently / previously used by the user) futures symbols as drop
 * downs on the DOM and charts"). Tradovate: click the symbol at the top of a module and type a
 * contract; colour links keep a DOM and a chart on one symbol.
 *
 * The dropdown has a search box, then **Recent** (the user's most recently used symbols with use
 * counts, synced per user in `settings.symbol_mru`, removable), then **Common futures** by asset
 * class. A root (`NQ`) means the front month, resolved live by the gateway
 * (`RequestFrontMonthContract`, → `NQZ6`); an explicit contract (`NQH7`) is kept as typed.
 *
 * Pure; integer counts and epoch-ms times only.
 */
import { FUTURES_CATALOG, catalogEntry, searchSymbols, type CatalogEntry } from "./watchlists/catalog.ts";

/** ⚙ Symbols kept in the synced MRU list. */
export const MAX_MRU_SYMBOLS = 20;
/** ⚙ Recent symbols shown in the dropdown. */
export const RECENT_SHOWN = 8;

/** One MRU entry: symbol, use count, last use (epoch ms). */
export type SymbolUse = { s: string; n: number; t: number };

const SYMBOL_RE = /^[A-Z0-9][A-Z0-9.]{0,15}$/;

/** Canonical form of a typed symbol (upper case, trimmed), or null when it cannot be one. */
export function canonicalSymbol(s: string): string | null {
  const v = s.trim().toUpperCase();
  return SYMBOL_RE.test(v) ? v : null;
}

/** The stored MRU read defensively: valid entries, one per symbol, newest first, bounded. */
export function sanitizeMru(v: unknown): SymbolUse[] {
  if (!Array.isArray(v)) return [];
  const out: SymbolUse[] = [];
  for (const x of v as unknown[]) {
    if (typeof x !== "object" || x === null) continue;
    const r = x as Record<string, unknown>;
    const s = typeof r["s"] === "string" ? canonicalSymbol(r["s"]) : null;
    const n = r["n"];
    const t = r["t"];
    if (s === null || typeof n !== "number" || typeof t !== "number") continue;
    if (!Number.isSafeInteger(n) || n < 1 || !Number.isSafeInteger(t) || t < 0) continue;
    if (out.some((e) => e.s === s)) continue;
    out.push({ s, n, t });
  }
  return out.sort((a, b) => b.t - a.t || b.n - a.n).slice(0, MAX_MRU_SYMBOLS);
}

/** Record one use of `symbol` at `nowMs`: count + 1, moved to the front. */
export function recordSymbolUse(mru: unknown, symbol: string, nowMs: number): SymbolUse[] {
  const s = canonicalSymbol(symbol);
  const list = sanitizeMru(mru);
  if (s === null) return list;
  const prev = list.find((e) => e.s === s);
  const t = Math.max(Math.trunc(nowMs), ...list.map((e) => e.t));
  const next: SymbolUse = { s, n: (prev?.n ?? 0) + 1, t };
  return [next, ...list.filter((e) => e.s !== s)].slice(0, MAX_MRU_SYMBOLS);
}

/** Remove `symbol` from the MRU (the × in the Recent section). */
export function removeSymbolUse(mru: unknown, symbol: string): SymbolUse[] {
  const s = canonicalSymbol(symbol);
  return sanitizeMru(mru).filter((e) => e.s !== s);
}

/** The Recent section: most recently used first, at most `max`. */
export function recentSymbols(mru: unknown, max = RECENT_SHOWN): SymbolUse[] {
  return sanitizeMru(mru).slice(0, Math.max(0, max));
}

/** Asset classes of the Common futures section, in display order. */
export const COMMON_FUTURES: readonly { group: string; roots: readonly string[] }[] = [
  { group: "Equity index", roots: ["NQ", "MNQ", "ES", "MES", "YM", "MYM", "RTY", "M2K"] },
  { group: "Energy", roots: ["CL", "MCL", "NG", "RB", "HO"] },
  { group: "Metals", roots: ["GC", "MGC", "SI", "SIL", "HG", "PL"] },
  { group: "Rates", roots: ["ZN", "ZB", "ZF", "ZT", "UB"] },
  { group: "FX", roots: ["6E", "6J", "6B", "6A", "6C", "6S"] },
  { group: "Ags", roots: ["ZC", "ZS", "ZW", "LE", "HE"] },
  { group: "Crypto", roots: ["MBT", "MET"] },
];

export type SymbolOption = {
  /** What is applied: a root (front month) or an explicit contract. */
  symbol: string;
  /** Catalog name, when known. */
  name: string;
  entry: CatalogEntry | null;
  /** MRU use count (Recent section only). */
  uses?: number;
};

export type SymbolSection = { id: string; title: string; options: SymbolOption[] };

function option(symbol: string, uses?: number): SymbolOption {
  const entry = catalogEntry(symbol);
  return { symbol, name: entry?.name ?? "", entry, ...(uses !== undefined ? { uses } : {}) };
}

/**
 * The dropdown's sections. No query: Recent, then the Common futures groups. With a query: the
 * catalog search (an explicit contract first, then roots), plus the typed text itself when HELM
 * does not know it (sent as typed; the gateway resolves or refuses it).
 */
export function symbolSections(query: string, mru: unknown): SymbolSection[] {
  const q = query.trim();
  if (q !== "") {
    const found = searchSymbols(q, 20).map((m) => option(m.symbol));
    const typed = canonicalSymbol(q);
    if (typed !== null && !found.some((o) => o.symbol === typed)) found.push(option(typed));
    return [{ id: "search", title: "Search", options: found }];
  }
  const out: SymbolSection[] = [];
  const recent = recentSymbols(mru).map((e) => option(e.s, e.n));
  if (recent.length > 0) out.push({ id: "recent", title: "Recent", options: recent });
  for (const g of COMMON_FUTURES) {
    out.push({ id: `common:${g.group}`, title: g.group, options: g.roots.map((r) => option(r)) });
  }
  return out;
}

/** Every option of `sections` in order (keyboard navigation walks this list). */
export function flatOptions(sections: readonly SymbolSection[]): SymbolOption[] {
  return sections.flatMap((s) => s.options);
}

/** Every Common futures root is in the catalog (static facts for the dropdown). */
export function commonRootsKnown(): boolean {
  const roots = new Set(FUTURES_CATALOG.map((e) => e.root));
  return COMMON_FUTURES.every((g) => g.roots.every((r) => roots.has(r)));
}

/**
 * What the one-click toggle does (SPEC §9.8, owner 2026-09-28): switching **off** is always one
 * click; switching **on** shows the explanation the first time only (then remembered in
 * `one_click_explained`).
 */
export function oneClickToggle(s: {
  oneClick: boolean;
  one_click_explained?: unknown;
}): { explain: true } | { explain: false; patch: { oneClick: boolean } } {
  if (s.oneClick) return { explain: false, patch: { oneClick: false } };
  if (s.one_click_explained !== true) return { explain: true };
  return { explain: false, patch: { oneClick: true } };
}
